import clsx from 'clsx';
import { type ReactElement, useMemo } from 'react';

import CardImage from '@/components/CardImage/CardImage';
import useCardLookup from '@/hooks/useCardLookup';
import { getDisplayImageUris } from '@/lib/cardUtils';
import type { MinimalCard } from '@/services/cardDatabase';

interface DraftCardGridProps {
  cards: MinimalCard[];
  onCardClick?: (cardId: string) => void;
  highlightedIds?: Set<string>;
  className?: string;
}

/**
 * Shared card grid for draft views (picking, review pool, timeline).
 * Uses full card cache for DFC image resolution.
 * Consistent 244px-based responsive grid with proper spacing.
 */
export default function DraftCardGrid({ cards, onCardClick, highlightedIds, className }: DraftCardGridProps): ReactElement {
  const { getFullCard } = useCardLookup();

  return (
    <div
      className={clsx('grid content-start justify-items-center gap-x-2 gap-y-4', className)}
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(244px, 100%), 1fr))', maxWidth: '1900px' }}
    >
      {cards.map((card, index) => (
        <DraftCardGridItem
          key={`${card.id}-${index}`}
          card={card}
          getFullCard={getFullCard}
          onClick={onCardClick ? () => onCardClick(card.id) : undefined}
          isHighlighted={highlightedIds?.has(card.id) ?? false}
        />
      ))}
    </div>
  );
}

interface DraftCardGridItemProps {
  card: MinimalCard;
  getFullCard: (id: string) => import('@/types/card').ScryfallCard | null;
  onClick?: () => void;
  isHighlighted: boolean;
}

function DraftCardGridItem({ card, getFullCard, onClick, isHighlighted }: DraftCardGridItemProps): ReactElement {
  const imageUris = useMemo(() => {
    const fullCard = getFullCard(card.id);
    if (fullCard) return getDisplayImageUris(fullCard);
    return card.image_uris;
  }, [card.id, card.image_uris, getFullCard]);

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={clsx(
          'w-fit cursor-pointer rounded-lg transition-all duration-150 hover:scale-[1.03] hover:ring-1 hover:ring-zinc-500',
          isHighlighted && 'ring-2 ring-amber-400 ring-offset-1 ring-offset-zinc-900',
        )}
        aria-label={`View details for ${card.name}`}
      >
        <CardImage imageUris={imageUris} name={card.name} size="normal" className="rounded-lg" />
      </button>
    );
  }

  return (
    <div className={clsx('w-fit rounded-lg', isHighlighted && 'ring-2 ring-amber-400 ring-offset-1 ring-offset-zinc-900')}>
      <CardImage imageUris={imageUris} name={card.name} size="normal" className="rounded-lg" />
    </div>
  );
}
